import {
    Box,
    CssBaseline,
    Toolbar,
    ThemeProvider,
    createTheme,
} from "@mui/material"

import {
    BrowserRouter as Router,
    Routes,
    Route,
    useLocation,
    useNavigate
} from "react-router-dom"

import { SnackbarProvider } from "./components/SnackbarContext"

import "@fontsource/inter/500.css"

import "./App.css"

import LandingHeader from "./components/LandingHeader"
import AppHeader from "./components/AppHeader"
import ScrollToTop from "./components/ScrollToTop"

import Landing from "./pages/Landing"
import LandingTestNow from "./pages/LandingTestNow"
import Home from "./pages/Home"
import Conversations from "./pages/Conversations"
import Settings from "./pages/settings/"
import Authentication from "./pages/Authentication"
import SSO from "./pages/SSO"

import ErrorBoundary from "./components/ErrorBoundary"
import { RequireAuth, PublicOnlyRoute } from "./components/RequireAuth"
import { isAuthenticated } from "./services/auth"
import { useLayoutEffect, useEffect, useRef } from "react"
import Loading from "./lib/loader"
import clarity from "@microsoft/clarity"

const CLARITY_LANDING = "v2irre0cb4"
const CLARITY_APP = "w0wvgzz0tl"
const CLARITY_LANDING_ROUTES = ["/home", "/login", "/teste_agora"]

const ClarityTracker = () => {
    const location = useLocation()
    const lastId = useRef(null)

    useEffect(() => {
        const id = CLARITY_LANDING_ROUTES.includes(location.pathname)
            ? CLARITY_LANDING
            : CLARITY_APP
        if (id !== lastId.current) {
            clarity.init(id)
            lastId.current = id
        }
    }, [location.pathname])

    return null
}

const theme = createTheme({
    typography: {
        fontFamily: "Inter, sans-serif",
        subtitle2: {
            color: "#6e6e6e",
        },
    },
    palette: {
        mode: "light",
        primary: {
            main: "#007bff" // matching design blue
        },
        secondary: {
            main: "#28a745" // green used for pill label
        },
        background: {
            default: "#fcfcfc"
        },
    }
})

const routes = [
    {
        "path": "/",
        "element": <Landing />
    },
    {
        "path": "/teste_agora",
        "element": <LandingTestNow />
    },
    {
        "path": "/auth",
        "element": <Authentication />
    },
    {
        "path": "/login",
        "element": <PublicOnlyRoute><LandingTestNow step={6} /></PublicOnlyRoute>
    },
    {
        "path": "/home",
        "element": <RequireAuth><Home /></RequireAuth>
    },
    {
        "path": "/conversas",
        "element": <RequireAuth><Conversations /></RequireAuth>
    },
    {
        "path": "/configuracoes/*",
        "element": <RequireAuth><Settings /></RequireAuth>
    },
    {
        "path": "/sso",
        "element": <SSO />
    },
]

const AppLayout = () => {
    const location = useLocation()
    const navigate = useNavigate()

    const landingPageRoutes = ["/", "/planos", "/teste_agora", "/auth", "/login"]
    const appRoutePrefixes = ["/home", "/conversas", "/configuracoes"]
    const showAppChrome = isAuthenticated() && appRoutePrefixes.some((route) => location.pathname.startsWith(route))
    const isLandingPage = landingPageRoutes.includes(location.pathname) || !showAppChrome

    useLayoutEffect(() => {
        if (isAuthenticated() && isLandingPage) {
            navigate('/home')
        }

    }, [isLandingPage, navigate])

    return (
        <>
            <Loading />
            <Box sx={{ display: "flex", minHeight: "100vh" }}>
                {isLandingPage ? <LandingHeader /> : <AppHeader />}
                <SnackbarProvider>
                    <Box
                        component="main"
                        className="content"
                        sx={{
                            width: "100%",
                        }}
                    >
                        <Toolbar className="app-header-spacer" />
                        <Routes>
                            {routes.map((item, index) => (
                                <Route
                                    key={index}
                                    path={item.path}
                                    element={item.element}
                                />
                            ))}
                        </Routes>
                    </Box>
                </SnackbarProvider>
            </Box>
        </>
    )
}

function App() {
    return (
        <ErrorBoundary
            componentName="App"
            fullScreen
            showReload
            logToBackend
            title="Erro na aplicação"
            message="Ocorreu um erro inesperado. Recarregue a página ou tente novamente."
        >
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Router basename={process.env.PUBLIC_URL}>
                    <ScrollToTop />
                    {process.env.REACT_APP_ENV === 'production' && <ClarityTracker />}
                    <AppLayout />
                </Router>
            </ThemeProvider>
        </ErrorBoundary>
    )
}

export default App
